import { io, Socket } from "socket.io-client";
import {
  AgentSocketEvent,
  ConnectionState,
  SocketEvent,
} from "@optidev/shared-types";
import {
  // Existing handlers
  handleChatResponse,
  handleToolStarted,
  handleToolCompleted,
  handleStreamComplete,
  handleAssistantEntrySaved,
  handleTaskCancelled,
  handleUserMessage,
  handleHitlPersisted,
  handleHitlActionClicked,
  handleTaskCreated,
  handleTaskPaused,
  handleTaskQueued,
  // NEW handlers
  handleConnectionStateChange,
  handleChatError,
  handleAutoCommitCreated,
  handleAutoCommitFailed,
  handleSlidesThumbnailsUpdated,
  handleSessionTitleUpdated,
  handleHitlRealtime,
  handleChatStarted,
  handleSetViewport,
  handlePreviewTestStep,
  // Circular dependency breaker
  setClaudeSocketManager,
} from "./agentEventHandlers";

/**
 * Singleton socket manager for Claude Agent
 * Ensures only one socket connection exists across component mounts/unmounts
 */
class ClaudeAgentSocketManager {
  private socket: Socket | null = null;
  private activeTaskId: string | null = null;
  private tokenGetter: (() => Promise<string | null>) | null = null;
  private workspaceIdGetter: (() => string | null) | null = null;
  private projectIdGetter: (() => string | undefined) | null = null;
  private sessionIdGetter: (() => string | null) | null = null;
  private criticalHandlersAttached: boolean = false;

  /**
   * Get or create socket connection
   * Uses callback-based auth to fetch fresh Firebase token on every connection/reconnection
   *
   * @param apiUrl - The API URL to connect to
   * @param getToken - Function that returns a fresh Firebase token (called on every connect/reconnect)
   * @param getWorkspaceId - Function that returns the active workspace ID (for platform admin support)
   * @param getProjectId - Function that returns the current project ID (for event filtering)
   * @param getSessionId - Function that returns the current session ID (for event filtering)
   */
  connect(
    apiUrl: string,
    getToken: () => Promise<string | null>,
    getWorkspaceId?: () => string | null,
    getProjectId?: () => string | undefined,
    getSessionId?: () => string | null,
  ): Socket {
    // Store getters for use in auth callback, extraHeaders, and event filtering
    this.tokenGetter = getToken;
    this.workspaceIdGetter = getWorkspaceId || null;
    this.projectIdGetter = getProjectId || null;
    this.sessionIdGetter = getSessionId || null;

    // Return existing socket if already connected
    if (this.socket && this.socket.connected) {
      console.log("[SocketManager] Reusing existing socket connection");
      return this.socket;
    }

    // If socket exists but disconnected, reconnect (auth callback will fetch fresh token)
    if (this.socket && !this.socket.connected) {
      console.log(
        "[SocketManager] Reconnecting existing socket (will fetch fresh token)",
      );
      this.socket.connect();
      return this.socket;
    }

    // Create new socket with callback-based auth
    // This ensures fresh token is fetched on every connection and reconnection attempt
    console.log("[SocketManager] Creating new socket connection");

    // Build extraHeaders with workspace ID if available
    const workspaceId = this.workspaceIdGetter?.();
    const extraHeaders: Record<string, string> = {};
    if (workspaceId) {
      extraHeaders["X-Workspace-Id"] = workspaceId;
    }

    this.socket = io(`${apiUrl}/claude-agent`, {
      auth: (cb) => {
        console.log(
          "[SocketManager] Auth callback invoked - fetching fresh token",
        );
        this.tokenGetter!().then((token) => {
          cb({ token });
        });
      },
      extraHeaders,
      transports: ["websocket"],
      // Reconnection settings for long-running Claude streaming connections
      // Note: pingTimeout/pingInterval are server-side only, client uses server's settings
      reconnection: true, // Auto-reconnect on disconnect
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 60000, // Connection timeout (matches server pingTimeout)
      // Socket.io delivery guarantees (at-least-once with server-side dedup)
      ackTimeout: 30000, // 30s per attempt (backend does heavy work before ack)
      retries: 5, // Retry up to 5 times (total 150s max)
    });

    // Handle connection recovery (Redis Streams adapter)
    // When server has connectionStateRecovery enabled, disconnected clients
    // can recover their session within 2 minutes without losing room memberships
    this.socket.on(SocketEvent.CONNECT, () => {
      // Note: socket.recovered is a Socket.io v4.6+ feature
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const socket = this.socket as any;
      if (socket.recovered) {
        console.log(
          "[SocketManager] Connection recovered - rooms auto-rejoined, no action needed",
        );
      } else {
        console.log("[SocketManager] Fresh connection established");
        // Fresh (not recovered) connection — any `slides-thumbnails-updated`
        // broadcasts that fired during the connect window (e.g. sidecar
        // legacy-migration auto-capture finishing while the socket was
        // still authenticating) were dropped by Socket.io. Proactively
        // re-sync slide state so the panel doesn't show stale thumbnails
        // until the next mutation or F5. Recovered connections (within
        // 2-min state-recovery window) replay their backlog automatically
        // so this is only needed on cold connects.
        //
        // Inlined via dynamic import to avoid a top-level circular dep
        // between claudeAgentSocket.ts and agentEventHandlers.ts.
        void handleSlidesThumbnailsUpdated({});
      }
      // Update connection state in store
      handleConnectionStateChange(ConnectionState.CONNECTED);
    });

    // Debug: Track Socket.io retry behavior
    this.socket.on(SocketEvent.CONNECT_ERROR, (error) => {
      console.log("[SocketManager] Connection error:", error.message);
    });

    this.socket.on(SocketEvent.DISCONNECT, (reason) => {
      console.log("[SocketManager] Disconnected:", reason);
      // Update connection state in store
      handleConnectionStateChange(ConnectionState.DISCONNECTED);
    });

    this.socket.on(SocketEvent.RECONNECT_ATTEMPT, (attempt) => {
      console.log("[SocketManager] Reconnect attempt:", attempt);
      // Update connection state in store
      handleConnectionStateChange(ConnectionState.RECONNECTING);
    });

    this.socket.on(SocketEvent.RECONNECT, (attempt) => {
      console.log("[SocketManager] Reconnected after", attempt, "attempts");
      // Connection state will be updated by 'connect' event
    });

    // Attach critical event handlers that persist across component unmounts
    // These update localStorage directly so state is correct when component remounts
    this.attachCriticalHandlers();

    return this.socket;
  }

  /**
   * Attach critical event handlers that persist across component unmounts
   * These update both Zustand store (source of truth) and localStorage (browser refresh recovery)
   * Uses shared handlers from agentEventHandlers.ts to avoid duplication
   */
  private attachCriticalHandlers(): void {
    if (!this.socket || this.criticalHandlersAttached) return;

    // Use shared handlers - updates both Zustand store AND localStorage
    // Zustand store is the source of truth for running tasks
    // localStorage is for browser refresh recovery only

    this.socket.on(AgentSocketEvent.CHAT_RESPONSE, (data) => {
      handleChatResponse(data);
    });

    this.socket.on(AgentSocketEvent.TOOL_STARTED, (data) => {
      console.log(
        "[SocketManager] Critical: tool-started received",
        data.toolName,
      );
      handleToolStarted(data);
    });

    this.socket.on(AgentSocketEvent.TOOL_COMPLETED, (data) => {
      console.log(
        "[SocketManager] Critical: tool-completed received",
        data.toolUseId?.slice(0, 8),
        "isError:",
        data.isError,
        "toolName:",
        data.toolName,
        "keys:",
        Object.keys(data),
      );
      handleToolCompleted(data);
    });

    this.socket.on(AgentSocketEvent.STREAM_COMPLETE, (data) => {
      console.log(
        "[SocketManager] Critical: stream-complete received",
        data.taskId?.slice(0, 8),
      );
      handleStreamComplete(data);

      // Clear active task in manager
      if (this.activeTaskId === data.taskId) {
        this.activeTaskId = null;
      }
    });

    // Assistant entry saved - updates message ID early (before stream-complete)
    this.socket.on(AgentSocketEvent.ASSISTANT_ENTRY_SAVED, (data) => {
      console.log(
        "[SocketManager] Critical: assistant-entry-saved received",
        data.taskId?.slice(0, 8),
      );
      handleAssistantEntrySaved(data);
    });

    this.socket.on(AgentSocketEvent.TASK_CANCELLED, (data) => {
      console.log(
        "[SocketManager] Critical: task-cancelled received",
        data.taskId?.slice(0, 8),
      );
      handleTaskCancelled(data);

      // Clear active task in manager
      if (this.activeTaskId === data.taskId) {
        this.activeTaskId = null;
      }
    });

    // Handlers consolidated from AgentModeContainer.tsx
    this.socket.on(AgentSocketEvent.USER_MESSAGE, (data) => {
      console.log(
        "[SocketManager] Critical: user-message received",
        data.id?.slice(0, 8),
      );
      handleUserMessage(data);
    });

    this.socket.on(AgentSocketEvent.TASK_CREATED, (data) => {
      console.log(
        "[SocketManager] Critical: task-created received",
        data.taskId?.slice(0, 8),
      );
      handleTaskCreated(data);
    });

    this.socket.on(AgentSocketEvent.TASK_QUEUED, (data) => {
      console.log(
        "[SocketManager] Critical: task-queued received",
        data.taskId?.slice(0, 8),
        data.reason,
      );
      handleTaskQueued(data);
    });

    this.socket.on(AgentSocketEvent.HITL_PERSISTED, (data) => {
      console.log(
        "[SocketManager] Critical: hitl-persisted received",
        data.messageId?.slice(0, 8),
      );
      handleHitlPersisted(data);
    });

    this.socket.on(AgentSocketEvent.HITL_ACTION_CLICKED, (data) => {
      console.log(
        "[SocketManager] Critical: hitl-action-clicked received",
        data.messageId?.slice(0, 8),
      );
      handleHitlActionClicked(data);
    });

    this.socket.on(AgentSocketEvent.TASK_PAUSED, (data) => {
      console.log(
        "[SocketManager] Critical: task-paused received",
        data.taskId?.slice(0, 8),
      );
      handleTaskPaused(data);
    });

    // Chat error - sent to client only (not room), requires sessionId
    this.socket.on(AgentSocketEvent.CHAT_ERROR, (data) => {
      console.log("[SocketManager] Chat error:", data.code);
      const sessionId = this.sessionIdGetter?.() || null;
      if (sessionId) {
        handleChatError(data, sessionId);
      }
    });

    // Auto-commit events
    this.socket.on(AgentSocketEvent.AUTO_COMMIT_CREATED, (data) => {
      console.log(
        "[SocketManager] Auto-commit created:",
        data.commitId?.slice(0, 8),
      );
      handleAutoCommitCreated(data);
    });

    this.socket.on(
      "slides-thumbnails-updated",
      (payload?: { projectId?: string; devContainerId?: string }) => {
        console.log("[SocketManager] Slides thumbnails updated", payload);
        handleSlidesThumbnailsUpdated(payload);
      },
    );

    this.socket.on(AgentSocketEvent.AUTO_COMMIT_FAILED, (data) => {
      console.log("[SocketManager] Auto-commit failed:", data.error);
      handleAutoCommitFailed(data);
    });

    // Session title update
    this.socket.on(AgentSocketEvent.SESSION_TITLE_UPDATED, (data) => {
      console.log(
        "[SocketManager] Session title updated:",
        data.sessionId?.slice(0, 8),
      );
      handleSessionTitleUpdated(data);
    });

    // HITL realtime (in-progress HITL content)
    this.socket.on(AgentSocketEvent.HITL_REALTIME, (data) => {
      console.log("[SocketManager] HITL realtime:", data.taskId?.slice(0, 8));
      handleHitlRealtime(data);
    });

    // Chat started - returns session info for room join
    this.socket.on(AgentSocketEvent.CHAT_STARTED, (data) => {
      console.log("[SocketManager] Chat started:", data.id?.slice(0, 8));
      handleChatStarted(data);
    });

    // Agent requested a preview viewport switch (signage/kiosk/custom dims)
    this.socket.on(AgentSocketEvent.SET_VIEWPORT, (data) => {
      console.log(
        "[SocketManager] Set viewport:",
        data?.type,
        `${data?.width}x${data?.height}`,
      );
      handleSetViewport(data);
    });

    // Agent ran a Puppeteer preview tool — drive the preview-side Test Window
    this.socket.on(AgentSocketEvent.PREVIEW_TEST_STEP, (data) => {
      handlePreviewTestStep(data);
    });

    this.criticalHandlersAttached = true;
    console.log(
      "[SocketManager] Critical event handlers attached (store + localStorage)",
    );
  }

  /**
   * Get existing socket (or null if not connected)
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Set active task ID (prevents disconnect during streaming)
   */
  setActiveTask(taskId: string | null): void {
    this.activeTaskId = taskId;
    console.log(
      "[SocketManager] Active task ID set to:",
      taskId?.slice(0, 8) || "null",
    );
  }

  /**
   * Get active task ID
   */
  getActiveTask(): string | null {
    return this.activeTaskId;
  }

  /**
   * Check if there's an active streaming task
   */
  hasActiveTask(): boolean {
    return this.activeTaskId !== null;
  }

  /**
   * Get current project ID (for event filtering)
   */
  getProjectId(): string | undefined {
    return this.projectIdGetter?.() || undefined;
  }

  /**
   * Get current session ID (for event filtering)
   */
  getSessionId(): string | null {
    return this.sessionIdGetter?.() || null;
  }

  /**
   * Soft disconnect - called on component unmount
   * Does NOT actually disconnect - keeps connection alive for navigation within project
   * Socket.IO ping/pong will keep connection alive across Agent <-> Visual Editor navigation
   */
  disconnect(): void {
    if (this.hasActiveTask()) {
      console.log(
        "[SocketManager] NOT disconnecting - active task in progress",
      );
      return;
    }

    // Don't disconnect on component unmount - keep connection alive for navigation
    // This preserves the same connection ID when user navigates Agent <-> Visual Editor
    console.log(
      "[SocketManager] Soft disconnect (keeping connection alive for navigation)",
    );
    // Previously: this.socket.disconnect();
    // Now: Do nothing - let the connection persist
  }

  /**
   * Force disconnect (used for cleanup)
   */
  forceDisconnect(): void {
    if (this.socket) {
      console.log("[SocketManager] Force disconnecting socket");
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.activeTaskId = null;
      this.criticalHandlersAttached = false;
    }
  }
}

// Export singleton instance
export const claudeSocketManager = new ClaudeAgentSocketManager();

// Register with event handlers to break circular dependency
setClaudeSocketManager(claudeSocketManager);
